/**
 * VRG-680 — customer docs and glossary entries gated on a launch switch.
 *
 * The publishers RECORD the gate (a guide's `feature:` frontmatter → `DocEntry.feature`; a
 * glossary entry's `feature`; `<!-- vera:feature x -->` blocks left in a body). This module
 * APPLIES it, at run time, against the switches `GET /features` reports — so flipping
 * `REFERRALS_ENABLED` on the API shows or hides the docs with no frontend redeploy.
 *
 * Fails CLOSED: an unknown feature name, or a switch that is anything but an explicit `true`,
 * hides the item. Every caller passes `FEATURE_SWITCHES_OFF` until the API has answered.
 *
 * Scope: the CUSTOMER docs, the brand corpus of the glossary, search and Ask. The staff
 * corpus and /internal/docs are never gated (decision 1 of VRG-680: internal stays visible).
 *
 * Honest limit: this hides, it does not withhold. The text is still in the published bundle;
 * the switch decides what a brand is SHOWN, which is the requirement.
 *
 * Dependency-free (one type import) so the guard spec loads it under node's own runner.
 */
import type { FeatureSwitches } from '../constants/featureSwitches'

export type GatedFeature = keyof FeatureSwitches

/** Is something tagged `feature` visible under these switches? Untagged → always. */
export const isFeatureVisible = (
  feature: string | undefined,
  switches: Partial<FeatureSwitches> | null | undefined,
): boolean => {
  if (!feature) return true
  return (switches as Record<string, unknown> | null | undefined)?.[feature] === true
}

/** Drop every item whose `feature` is switched off. Returns the SAME array when nothing is gated. */
export const withoutGated = <T extends { feature?: string }>(
  items: readonly T[],
  switches: Partial<FeatureSwitches> | null | undefined,
): readonly T[] =>
  items.some(i => !isFeatureVisible(i.feature, switches))
    ? items.filter(i => isFeatureVisible(i.feature, switches))
    : items

const OPEN_RE = /^<!-- vera:feature ([a-z][a-z0-9-]*) -->$/
const CLOSE = '<!-- /vera:feature -->'

/**
 * Resolve `<!-- vera:feature x -->` … `<!-- /vera:feature -->` blocks in a published body.
 *
 * The marker lines are ALWAYS blanked (react-markdown would print a comment as text). A block
 * whose switch is off is blanked whole. Lines are replaced, never removed: heading anchors are
 * keyed by the line number the publisher recorded, and removing a line would shift every
 * heading below it off its anchor. The publisher refuses a heading inside a block, so nothing
 * the manifest advertises can be blanked here.
 */
export const applyFeatureBlocks = (
  body: string,
  switches: Partial<FeatureSwitches> | null | undefined,
): string => {
  if (!body.includes('<!-- vera:feature')) return body
  let open: string | null = null
  return body
    .split('\n')
    .map(line => {
      const s = line.trim()
      const m = OPEN_RE.exec(s)
      if (m) {
        open = m[1]
        return ''
      }
      if (s === CLOSE) {
        open = null
        return ''
      }
      return open && !isFeatureVisible(open, switches) ? '' : line
    })
    .join('\n')
}
